import React from 'react'
import { isNull } from 'lodash'
import {
  TestSessions_testSessions_testSessions,
  TestSessions_testSessions_pagination,
  SortDirectionEnum,
  getFormattedDateAndTime,
  TestType,
  useGenerateNewInvoice,
  SortByType,
  CategoryTable,
} from '@modmd/data'
import { TestSessionStatusLabel } from 'sharedComponents/TestSessionStatusLabel'
import { TestSessionPaymentLabel } from 'sharedComponents/TestSessionPaymentLabel'
import { Box } from 'components/Layout'
import Datalist, { DataListProps } from 'components/Datalist'
import Pagination from 'components/Pagination'
import Card from 'components/Card'
import { useIsMinDevice } from 'utils/hooks/useMedia'
import { DEVICE } from 'theme'
import Label from 'components/Label'
import { Checkbox } from 'components/Checkbox'
import Button from 'components/Button'
import { ConfirmationDialog } from 'components/Dialog'
import { Text } from 'components/Typography'
import { useToaster } from 'utils/useToaster'
import { checkLaunchPopupError, launchPopup } from 'utils/helpers'
import { invoiceDateOrFail } from 'internal-portal/routes/FinancesList/utils'

interface ColumnLabels {
  date?: string | null
  time?: string | null
  location?: string | null
  sublocation?: string | null
  numOfPeople?: string | null
  company?: string | null
  group?: string | null
  status?: string | null
  paymentStatus?: string | null
  invoiceCheck?: string | null
  invoiceDate?: string | null
  autoInvoice?: string | null
}

interface Props {
  tableHeader?: React.ReactElement
  tableFooter?: React.ReactElement
  testSessions: TestSessions_testSessions_testSessions[]
  sorter?: DataListProps['sorter']
  columnLabels?: ColumnLabels
  pagination?: TestSessions_testSessions_pagination
  isCard?: boolean
  isLoading?: boolean
  hasPaginationInFooter?: boolean
  onPageChange?: (page: number) => void
  onSortChange?: (sortBy: string, direction: SortDirectionEnum) => void
  onRowClick: (data: TestSessions_testSessions_testSessions) => void
  pageLength?: number
  listOfInvoices?: Array<string>
  setListOfInvoices?: React.Dispatch<React.SetStateAction<Array<string>>>
  testSessionRefetch?: () => void
}

export const PAGE_LENGTH = 10

const defaultColumnLabels = {
  id: 'Session Id',
  date: 'Date',
  time: 'Time',
  name: 'Name',
  location: 'Location',
  sublocation: 'Rate type',
  numOfPeople: 'Number of people',
  company: 'Company',
  group: 'Group',
  status: 'Status',
  paymentStatus: 'Invoice Number',
  invoiceCheck: 'Invoice',
  invoiceDate: null,
  autoInvoce: null,
}

export const TestSessionsDatalist: React.VFC<Props> = ({
  tableHeader,
  tableFooter,
  testSessions,
  sorter,
  pagination,
  columnLabels: providedColumnLabels,
  isCard = true,
  isLoading,
  hasPaginationInFooter = false,
  onSortChange,
  onPageChange,
  onRowClick,
  pageLength = PAGE_LENGTH,
  listOfInvoices,
  setListOfInvoices,
  testSessionRefetch,
}) => {
  const [confirmModal, setConfirmModal] = React.useState(false)
  const [invoiceData, setInvoiceData] = React.useState({
    invoiceDocNumber: '',
    invoiceId: '',
    testSessionId: '',
  })
  const isDesktop = useIsMinDevice(DEVICE.DESKTOP)
  const isTablet = useIsMinDevice(DEVICE.TABLET)
  const [newInvoice, { loading: isLoadingNewInvoice }] = useGenerateNewInvoice()
  const columnLabels = { ...defaultColumnLabels, ...(providedColumnLabels || {}) }
  const numOfColumns = Object.values(columnLabels).filter((label) => label !== null).length
  const rowGridTemplateColumns = React.useMemo(() => `repeat(${numOfColumns}, 1fr)`, [numOfColumns])
  const paginationComp = pagination && pagination.total > pageLength && onPageChange && (
    <Pagination
      firstPage={1}
      lastPage={Math.ceil(pagination.total / pageLength) || 1}
      currentPage={Math.ceil(pagination.from / pageLength) + 1}
      onPageChange={onPageChange}
    />
  )
  const addOrRemoveInvoice = (id: string) => {
    if (setListOfInvoices && listOfInvoices)
      if (listOfInvoices.includes(id))
        setListOfInvoices(listOfInvoices.filter((item) => item !== id))
      else setListOfInvoices([...listOfInvoices, id])
  }
  const { setToastMessage } = useToaster()

  const showConfirmGenerateInvoice = (invoiceId = '', invoiceDocNumber = '') => {
    const testSessionsIds = testSessions
      .filter((ts) => ts.Invoice?.invoiceId === invoiceId)
      .map((ts) => ts.id)
      .join(',')
    setInvoiceData({
      invoiceDocNumber,
      invoiceId,
      testSessionId: testSessionsIds,
    })
    setConfirmModal(true)
  }

  const dismissConfirmGenerateInvoice = () => {
    setConfirmModal(false)
    setInvoiceData({
      invoiceDocNumber: '',
      invoiceId: '',
      testSessionId: '',
    })
  }

  const generateOtherInvoice = async (id: string) => {
    try {
      await newInvoice({
        variables: {
          inputData: id,
        },
      })
      setConfirmModal(false)
      setInvoiceData({
        invoiceDocNumber: '',
        invoiceId: '',
        testSessionId: '',
      })
      if (testSessionRefetch) {
        void testSessionRefetch()
      }
    } catch (e) {
      setToastMessage('No Quickbooks connection. Please login', 'danger')
      if (checkLaunchPopupError(String(e).split('Error: ')[1])) {
        launchPopup(String(e).split('Error: ')[1])
      }
    }
  }

  const Wrapper = isCard ? Card : Box
  return (
    <>
      <Wrapper>
        <Datalist
          isLoading={isLoading}
          skeletonProps={{
            columns: numOfColumns,
            rows: pageLength,
          }}
          data={testSessions}
          sorter={sorter}
          onSortChange={onSortChange}
          header={tableHeader}
          footer={hasPaginationInFooter && paginationComp ? paginationComp : tableFooter}
          shouldRenderTableHeader={isDesktop}
          showActionIndicators
          renderTableHeader={
            isDesktop
              ? () => (
                  <Datalist.Row gridTemplateColumns={rowGridTemplateColumns}>
                    {!isNull(columnLabels.id) ? (
                      <Datalist.HeaderCell label={columnLabels.id} sortKey={SortByType.ID} />
                    ) : null}
                    {!isNull(columnLabels.invoiceCheck) ? (
                      <Datalist.HeaderCell
                        label={columnLabels.invoiceCheck}
                        sortKey={SortByType.INVOICE}
                      />
                    ) : null}
                    {!isNull(columnLabels.date) ? (
                      <Datalist.HeaderCell label={columnLabels.date} sortKey={SortByType.DATE} />
                    ) : null}
                    {!isNull(columnLabels.time) ? (
                      <Datalist.HeaderCell label={columnLabels.time} />
                    ) : null}
                    {!isNull(columnLabels.name) ? (
                      <Datalist.HeaderCell label={columnLabels.name} />
                    ) : null}
                    {!isNull(columnLabels.location) ? (
                      <Datalist.HeaderCell label={columnLabels.location} />
                    ) : null}
                    {!isNull(columnLabels.sublocation) ? (
                      <Datalist.HeaderCell label={columnLabels.sublocation} />
                    ) : null}
                    {!isNull(columnLabels.numOfPeople) ? (
                      <Datalist.HeaderCell label={columnLabels.numOfPeople} />
                    ) : null}
                    {!isNull(columnLabels.company) ? (
                      <Datalist.HeaderCell label={columnLabels.company} />
                    ) : null}
                    {!isNull(columnLabels.group) ? (
                      <Datalist.HeaderCell label={columnLabels.group} />
                    ) : null}
                    {!isNull(columnLabels.status) ? (
                      <Datalist.HeaderCell label={columnLabels.status} />
                    ) : null}
                    {!isNull(columnLabels.paymentStatus) ? (
                      <Datalist.HeaderCell
                        label={columnLabels.paymentStatus}
                        sortKey={SortByType.INVOICE_DOC_NUMBER}
                      />
                    ) : null}
                    {!isNull(columnLabels.autoInvoice) ? (
                      <Datalist.HeaderCell label={columnLabels?.autoInvoice} />
                    ) : null}
                    {!isNull(columnLabels.invoiceDate) ? (
                      <Datalist.HeaderCell label={columnLabels?.invoiceDate} />
                    ) : null}
                  </Datalist.Row>
                )
              : undefined
          }
          renderTableRow={(data) => {
            const {
              id,
              date,
              name,
              Location,
              Members,
              paymentStatus,
              type,
              Invoice,
              finishedAt,
              sublocation,
            } = data
            return (
              <Datalist.Row
                gridTemplateColumns={{
                  _: 'repeat(2, 1fr)',
                  DESKTOP: rowGridTemplateColumns,
                }}
                {...(!isNull(columnLabels.invoiceCheck) ? {} : { onClick: () => onRowClick(data) })}
                {...(!isTablet ? { alignItems: 'start' } : {})}
              >
                {!isNull(columnLabels.id) ? (
                  <Datalist.Cell label={columnLabels.id}>{id}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.invoiceCheck) ? (
                  <Datalist.Cell label={columnLabels.invoiceCheck}>
                    {Invoice?.invoiceId || !finishedAt ? (
                      <>
                        {Invoice?.invoiceId && (
                          <Button
                            appearance="ghost"
                            size="small"
                            onClick={() =>
                              showConfirmGenerateInvoice(
                                Invoice?.invoiceId as string,
                                Invoice?.invoiceDocNumber as string
                              )
                            }
                            style={{ whiteSpace: 'initial', height: '100%', width: 113 }}
                          >
                            {Invoice?.invoiceDocNumber
                              ? `Invoice NO: ${Invoice?.invoiceDocNumber}`
                              : `Invoice ID: ${Invoice?.invoiceId}`}
                          </Button>
                        )}
                        {!Invoice?.invoiceId && (
                          <Label size="l" indicator="grayDark">
                            Not finished yet
                          </Label>
                        )}
                      </>
                    ) : (
                      <Checkbox
                        checked={listOfInvoices && listOfInvoices.includes(id)}
                        onChange={() => addOrRemoveInvoice(id)}
                        label=""
                        hasBottomContent={false}
                      />
                    )}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.date) ? (
                  <Datalist.Cell label={columnLabels.date}>
                    {getFormattedDateAndTime(date)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.time) ? (
                  <Datalist.Cell label={columnLabels.time}>
                    {getFormattedDateAndTime(date, true, false)}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.name) ? (
                  <Datalist.Cell label={columnLabels.name}>
                    {type === TestType.SofiaRsv && <Label indicator="grayDark">RSV</Label>}
                    {type === TestType.RapidCovid19 && <Label indicator="grayDark">Rapid</Label>}
                    {type === TestType.Covid19 && <Label indicator="grayDark">PCR</Label>}
                    {(type === TestType.Antigen ||
                      type === TestType.AntigenCareStart ||
                      type === TestType.SiennaAntigen ||
                      type === TestType.IndicaidAntigen) && (
                      <Label indicator="grayDark">Antigen</Label>
                    )}
                    {type === TestType.Lucira && <Label indicator="grayDark">Lucira</Label>}
                    {CategoryTable[type] === 'Strep' && <Label indicator="grayDark">Strep</Label>}
                    {CategoryTable[type] === 'Influenza' && (
                      <Label indicator="grayDark">Influenza</Label>
                    )}
                    {name ?? 'unnamed'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.location) ? (
                  <Datalist.Cell label={columnLabels.location}>
                    {Location.street}, {Location.city}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.sublocation) ? (
                  <Datalist.Cell label={columnLabels.sublocation}>{sublocation}</Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.numOfPeople) ? (
                  <Datalist.Cell label={columnLabels.numOfPeople}>
                    {
                      Members.filter(({ isParticipant, isResulted }) => isParticipant && isResulted)
                        .length
                    }{' '}
                    / {Members.filter(({ isParticipant }) => isParticipant).length}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.company) ? (
                  <Datalist.Cell label={columnLabels.company}>
                    {data?.Company?.name || ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.group) ? (
                  <Datalist.Cell label={columnLabels.group}>
                    {data?.Group?.name}
                    {(!data?.Group && data?.Groups?.map(({ name }) => name).join(', ')) || ''}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.status) ? (
                  <Datalist.Cell label={columnLabels.status}>
                    <TestSessionStatusLabel session={data} />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.paymentStatus) ? (
                  <Datalist.Cell label={columnLabels.paymentStatus}>
                    <TestSessionPaymentLabel
                      paymentStatus={paymentStatus}
                      invoices={Invoice?.invoiceDocNumber ?? Invoice?.invoiceId}
                      isInvoiceDocNumber={!!Invoice?.invoiceDocNumber}
                    />
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.autoInvoice) && columnLabels.autoInvoice ? (
                  <Datalist.Cell label={columnLabels.autoInvoice}>
                    {Invoice?.autoInvoice ? 'Automatic' : 'Manual'}
                  </Datalist.Cell>
                ) : null}
                {!isNull(columnLabels.invoiceDate) && columnLabels.invoiceDate ? (
                  <Datalist.Cell label={columnLabels.invoiceDate}>
                    {invoiceDateOrFail(Invoice?.invoiceDate)}
                  </Datalist.Cell>
                ) : null}
              </Datalist.Row>
            )
          }}
        />
      </Wrapper>
      {!hasPaginationInFooter && paginationComp && (
        <Pagination.Wrapper>{paginationComp}</Pagination.Wrapper>
      )}
      <ConfirmationDialog
        title="Generate invoice"
        isOpen={confirmModal}
        onDismiss={dismissConfirmGenerateInvoice}
        actions={
          <>
            <Button onClick={dismissConfirmGenerateInvoice} colorVariant="secondary">
              Cancel
            </Button>
            <Button
              onClick={() => generateOtherInvoice(invoiceData.invoiceId)}
              isFetching={isLoadingNewInvoice}
            >
              Cancel and Generate
            </Button>
          </>
        }
      >
        <>
          <Text>Do you want to cancel this invoice and generate a new one? </Text>
          {invoiceData.testSessionId && invoiceData.invoiceId && (
            <Text>
              <strong>Session:</strong> {invoiceData.testSessionId}{' '}
              {invoiceData.invoiceDocNumber ? (
                <>
                  <strong>Invoice NO:</strong> {invoiceData.invoiceDocNumber}
                </>
              ) : (
                <>
                  <strong>Invoice ID:</strong> {invoiceData.invoiceId}
                </>
              )}
            </Text>
          )}
        </>
      </ConfirmationDialog>
    </>
  )
}
